import { useQuery } from '@tanstack/react-query';
import {
  articlesAPI,
  suppliersAPI,
  clientsAPI,
  servicesAPI,
  demandeursAPI,
  projetsAPI,
  userAPI,
} from '../../services/api';
import { Article } from '../../types';
import { Supplier } from '../../types/supplier';
import { Client } from '../../types/client';
import { Service } from '../../types/service';
import { Demandeur } from '../../types/demandeur';
import { Projet } from '../../types/projet';
import { User } from '../../types/auth';

/**
 * Cached dropdown / reference-data hooks.
 *
 * These back the article/supplier/client/... pickers that almost every form
 * needs. Previously each page re-fetched them via a plain useEffect on EVERY
 * mount, so navigating between pages re-downloaded them every time (the cause
 * of "fetches on every nav even when nothing changed").
 *
 * Now they are fetched once and served from cache. staleTime is Infinity
 * because this reference data only changes through explicit CRUD — and when it
 * does, the backend's realtime-invalidation broadcast invalidates the matching
 * root key (e.g. ['articles']), which refreshes the dropdown too. So: never
 * re-fetched on navigation, always refreshed the moment the data actually
 * changes.
 *
 * The key root MUST be the entity slug so those broadcasts match.
 */
export function useArticlesDropdown() {
  return useQuery<Article[]>({
    queryKey: ['articles', 'dropdown'],
    queryFn: () => articlesAPI.getAllForDropdown(),
    staleTime: Infinity,
  });
}

export function useSuppliersDropdown() {
  return useQuery<Supplier[]>({
    queryKey: ['suppliers', 'dropdown'],
    queryFn: () => suppliersAPI.getAllForDropdown(),
    staleTime: Infinity,
  });
}

export function useClientsDropdown() {
  return useQuery<Client[]>({
    queryKey: ['clients', 'dropdown'],
    queryFn: () => clientsAPI.getAllForDropdown(),
    staleTime: Infinity,
  });
}

export function useServicesDropdown() {
  return useQuery<Service[]>({
    queryKey: ['services', 'dropdown'],
    queryFn: () => servicesAPI.getAllForDropdown(),
    staleTime: Infinity,
  });
}

export function useDemandeursDropdown() {
  return useQuery<Demandeur[]>({
    queryKey: ['demandeurs', 'dropdown'],
    queryFn: () => demandeursAPI.getAllForDropdown(),
    staleTime: Infinity,
  });
}

export function useProjetsDropdown() {
  return useQuery<Projet[]>({
    queryKey: ['projets', 'dropdown'],
    queryFn: () => projetsAPI.getAllForDropdown(),
    staleTime: Infinity,
  });
}

/**
 * Users for the "demandeur" filter (managers only). `enabled` lets a page skip
 * the fetch entirely for non-managers. Shared cache key means Demandes d'Achat
 * and Bons de Commande reuse the same result (deduped, cached across visits).
 */
export function useUsersDropdown(enabled = true) {
  return useQuery<User[]>({
    queryKey: ['users', 'dropdown'],
    queryFn: () => userAPI.getUsers(0, 500),
    staleTime: Infinity,
    enabled,
  });
}
